import { useEffect, useState } from 'react'
import type { RatingConfigTree, RatingCatalog, RatingLevel, RatingNode, RatingPreview } from '../types'
import { getRatingConfig, saveRatingConfig, resetRatingConfig, getRatingCatalog, previewRating } from '../services/ratingConfigApi'

export function ReportRatingEditor() {
  const [cfg, setCfg] = useState<RatingConfigTree | null>(null)
  const [cat, setCat] = useState<RatingCatalog | null>(null)
  const [ags, setAgs] = useState('09162000')
  const [pv, setPv] = useState<RatingPreview | null>(null)
  const [status, setStatus] = useState('')

  useEffect(() => { getRatingConfig().then((r) => setCfg(r.config)); getRatingCatalog().then(setCat) }, [])
  if (!cfg || !cat) return <div className="p-6">Lädt…</div>

  const update = (fn: (c: RatingConfigTree) => RatingConfigTree) => setCfg(fn(structuredClone(cfg)))
  const onSave = async () => { try { await saveRatingConfig(cfg); setStatus('Gespeichert.') } catch (e: any) { setStatus('Fehler: ' + (e?.response?.data?.detail ?? e.message)) } }
  const onReset = async () => { const r = await resetRatingConfig(); setCfg(r.config); setStatus('Zurückgesetzt.') }
  const onPreview = async () => setPv(await previewRating(ags))

  const renderLevel = (levelKey: 'makro' | 'mikro', level: RatingLevel) => (
    <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 space-y-2">
      <div className="text-sm font-semibold text-gut8-anthracite">{levelKey} — root: {level.root}</div>
      {level.nodes.map((n, ni) => (
        <NodeForm key={ni} node={n} nodeKeys={level.nodes.map((x) => x.key)} cat={cat}
          onChange={(nn) => update((c) => { c[levelKey].nodes[ni] = nn; return c })}
          onRemove={() => update((c) => { c[levelKey].nodes.splice(ni, 1); return c })} />
      ))}
      <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => update((c) => { c[levelKey].nodes.push({ key: 'neuer_node', type: 'weighted', scale: 'quality', components: [] }); return c })}>+ Node</button>
    </div>
  )

  return (
    <div className="p-6 space-y-4">
      <div className="flex gap-2 items-center">
        <h1 className="text-2xl font-bold text-gut8-anthracite flex-1">Wertungen (Rating-Config)</h1>
        <input className="w-32 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={ags} onChange={(e) => setAgs(e.target.value)} />
        <button className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted" onClick={onPreview}>Vorschau</button>
        <button className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 border border-border-gut8-medium bg-white text-gut8-anthracite text-sm hover:bg-gut8-bg-muted" onClick={onReset}>Zurücksetzen</button>
        <button className="inline-flex items-center gap-2 px-4 h-9 rounded-gut8 bg-primary text-text-inverse font-semibold text-sm hover:bg-primary-hover disabled:opacity-40" onClick={onSave}>Speichern</button>
      </div>
      {status && <div className="text-sm text-text-muted">{status}</div>}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-4">
          {renderLevel('makro', cfg.makro)}
          {renderLevel('mikro', cfg.mikro)}
          <ScaleTables cfg={cfg} update={update} />
        </div>
        <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 overflow-auto">
          <h2 className="text-sm font-semibold text-gut8-anthracite mb-2">Vorschau (Scores + Terms)</h2>
          {pv ? (
            <>
              {(['makro', 'mikro'] as const).map((lvl) => (
                <div key={lvl}><h3 className="text-sm font-semibold text-gut8-anthracite mt-2">{lvl}</h3>
                  <table className="text-xs w-full"><tbody>
                    {Object.entries(pv[lvl]).map(([k, v]) => (
                      <tr key={k}><td className="pr-2 text-text-muted">{k}</td>
                        <td className="text-right tabular-nums text-gut8-anthracite">{v?.score ?? '–'}</td>
                        <td className="pl-2 text-text-muted">{v?.term ?? ''}</td></tr>
                    ))}
                  </tbody></table>
                </div>
              ))}
            </>
          ) : <div className="text-sm text-text-muted">„Vorschau" klicken.</div>}
        </div>
      </div>
    </div>
  )
}

function NodeForm({ node, nodeKeys, cat, onChange, onRemove }:
  { node: RatingNode; nodeKeys: string[]; cat: RatingCatalog; onChange: (n: RatingNode) => void; onRemove: () => void }) {
  const set = (patch: Partial<RatingNode>) => onChange({ ...node, ...patch })
  return (
    <div className="bg-gut8-bg-muted border border-border-gut8-light rounded-gut8 p-3 space-y-2">
      <div className="flex gap-2 items-center">
        <input className="w-48 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={node.key} onChange={(e) => set({ key: e.target.value })} />
        <select className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={node.type} onChange={(e) => set({ type: e.target.value as RatingNode['type'] })}>
          {cat.node_types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <select className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={node.scale ?? 'none'} onChange={(e) => set({ scale: e.target.value })}>
          <option value="none">(keine Skala)</option>
          {cat.scales.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <button className="text-sm text-secondary hover:opacity-80 ml-auto" onClick={onRemove}>✕</button>
      </div>
      {node.type === 'quantile' && (
        <div className="flex gap-2 items-center text-sm">
          <select className="px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={node.match ?? 'name'} onChange={(e) => set({ match: e.target.value as RatingNode['match'] })}>
            {cat.matches.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input className="flex-1 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" placeholder="Indikator (Name/Code/Calc-Name)"
                 value={node.indicator ?? ''} onChange={(e) => set({ indicator: e.target.value })} list="rating-indicators" />
          <label className="flex items-center gap-1 text-gut8-anthracite"><input type="checkbox" checked={!!node.higher_is_better}
            onChange={(e) => set({ higher_is_better: e.target.checked })} /> höher=besser</label>
        </div>
      )}
      {node.type === 'prognos_note' && (
        <input className="w-full px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" placeholder="rank_field (rang_demografie/rang_wettbewerb/gesamtrang)"
               value={node.rank_field ?? ''} onChange={(e) => set({ rank_field: e.target.value })} />
      )}
      {node.type === 'weighted' && (
        <div className="space-y-1">
          {(node.components ?? []).map((c, ci) => (
            <div key={ci} className="flex gap-2 items-center text-sm">
              <select className="flex-1 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={c.ref}
                onChange={(e) => { const comps = [...(node.components ?? [])]; comps[ci] = { ...c, ref: e.target.value }; set({ components: comps }) }}>
                <option value="">(ref wählen)</option>
                {nodeKeys.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input className="w-20 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" type="number" value={c.weight}
                onChange={(e) => { const comps = [...(node.components ?? [])]; comps[ci] = { ...c, weight: Number(e.target.value) }; set({ components: comps }) }} />
              <button className="text-sm text-secondary hover:opacity-80" onClick={() => { const comps = [...(node.components ?? [])]; comps.splice(ci, 1); set({ components: comps }) }}>✕</button>
            </div>
          ))}
          <button className="text-sm font-medium text-primary hover:text-primary-hover" onClick={() => set({ components: [...(node.components ?? []), { ref: '', weight: 0 }] })}>+ Komponente</button>
        </div>
      )}
    </div>
  )
}

function ScaleTables({ cfg, update }: { cfg: RatingConfigTree; update: (fn: (c: RatingConfigTree) => RatingConfigTree) => void }) {
  return (
    <div className="bg-white border border-border-gut8-light rounded-gut8 shadow-gut8-sm p-4 space-y-3">
      <div className="text-sm font-semibold text-gut8-anthracite">Wort-Skalen</div>
      {Object.entries(cfg.scales).map(([name, rows]) => (
        <div key={name}>
          <div className="text-sm font-medium text-gut8-anthracite">{name}</div>
          {rows.map((r, ri) => (
            <div key={ri} className="flex gap-2 text-sm">
              <input className="w-20 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" type="number" value={r[0]}
                onChange={(e) => update((c) => { c.scales[name][ri][0] = Number(e.target.value); return c })} />
              <input className="flex-1 px-3 py-2 text-sm border border-border-gut8-medium rounded-gut8 bg-white focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" value={r[1]}
                onChange={(e) => update((c) => { c.scales[name][ri][1] = e.target.value; return c })} />
            </div>
          ))}
        </div>
      ))}
      <datalist id="rating-indicators">
        {/* optional: could map cat.indicators here for autocomplete */}
      </datalist>
    </div>
  )
}
